import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Users, HelpCircle, ShieldAlert, Scale, TriangleAlert, MessagesSquare, CircleHelp,
  Lock, ArrowLeft, Copy, CheckCircle2, ShieldCheck, Loader2, Calendar,
} from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CATEGORIES, CAMPUSES } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";

const ICONS = { Users, HelpCircle, ShieldAlert, Scale, TriangleAlert, MessagesSquare, CircleHelp };

export default function Report() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { content } = useContent();

  const [category, setCategory] = useState(params.get("category") || "");
  const [anonymous, setAnonymous] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", campus: "", reporter_name: "", reporter_email: "",
    involves_others: "", desired_outcome: "", meeting_mode: "",
  });
  const [requestMeeting, setRequestMeeting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, reporter_name: f.reporter_name || user.name, reporter_email: f.reporter_email || user.email, campus: f.campus || user.campus }));
  }, [user]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!category) return toast.error("Please choose what your concern is about.");
    if (form.title.trim().length < 3) return toast.error("Please add a short title.");
    if (form.description.trim().length < 10) return toast.error("Please describe your concern a little more.");
    setSubmitting(true);
    try {
      const payload = {
        category,
        title: form.title,
        description: form.description,
        campus: form.campus,
        is_anonymous: anonymous,
        reporter_name: anonymous ? "" : form.reporter_name,
        reporter_email: anonymous ? "" : form.reporter_email,
        involves_others: form.involves_others,
        desired_outcome: form.desired_outcome,
        request_meeting: requestMeeting,
        meeting_mode: requestMeeting ? form.meeting_mode : "",
      };
      const { data } = await api.post("/concerns", payload);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-20">
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl border border-forest-600/10 shadow-lg p-8 sm:p-12 text-center">
            <span className="grid place-items-center h-16 w-16 rounded-full bg-forest-50 text-forest-600 mx-auto">
              <CheckCircle2 className="h-8 w-8" strokeWidth={1.5} />
            </span>
            <h1 className="mt-6 font-serif text-3xl text-forest-900">Thank you for trusting us.</h1>
            <p className="mt-3 text-charcoal-600">Your concern has been received. A trained specialist will review it with care and confidentiality.</p>

            <div className="mt-8 rounded-2xl bg-bone-100 border border-forest-600/10 p-6 text-left">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-forest-600">Keep these safe</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <CopyField label="Case number" value={result.case_number} testid="result-case-number" />
                <CopyField label="Access code" value={result.access_code} testid="result-access-code" />
              </div>
              <p className="mt-4 text-sm text-charcoal-600">Use them on the tracking page to check your concern's status anytime.</p>
            </div>

            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <Button onClick={() => navigate("/track")} data-testid="result-track-btn" className="rounded-full bg-forest-600 hover:bg-forest-700 text-bone px-6">Track this concern</Button>
              {user ? (
                <Button variant="secondary" onClick={() => navigate("/dashboard")} className="rounded-full bg-bone-100 hover:bg-bone-200 text-forest-800 border border-forest-600/10 px-6">Go to my dashboard</Button>
              ) : (
                <Button variant="secondary" onClick={() => navigate("/")} className="rounded-full bg-bone-100 hover:bg-bone-200 text-forest-800 border border-forest-600/10 px-6">Return home</Button>
              )}
            </div>
          </motion.div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-forest-800 mb-6">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to home
        </Link>
        <span className="text-xs font-bold tracking-[0.2em] uppercase text-terracotta-500">Confidential intake</span>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-forest-900 leading-tight">Share your concern</h1>
        <p className="mt-4 text-charcoal-600 max-w-xl">Take your time. There are no wrong answers — a specialist will follow up and help from here.</p>

        <div className="mt-6 flex items-center gap-3 rounded-2xl bg-forest-50 border border-forest-200 p-4">
          <ShieldCheck className="h-6 w-6 text-forest-600 shrink-0" strokeWidth={1.5} />
          <p className="text-sm text-forest-800">Handled with discretion. Information is shared only with those who need it to help resolve your concern.</p>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-10">
          {/* Anonymous */}
          <div className="flex items-center justify-between rounded-2xl bg-white border border-forest-600/10 p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <Lock className="h-5 w-5 text-forest-600 mt-0.5" strokeWidth={1.5} />
              <div>
                <p className="font-semibold text-forest-900">Submit anonymously</p>
                <p className="text-sm text-charcoal-600">We won't ask for your name or email. Note: we won't be able to email you updates.</p>
              </div>
            </div>
            <Switch checked={anonymous} onCheckedChange={setAnonymous} data-testid="anonymous-toggle" />
          </div>

          {/* Category */}
          <div>
            <Label className="text-forest-900 font-semibold text-base">What is your concern about? <span className="text-terracotta-500">*</span></Label>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {CATEGORIES.map((c) => {
                const Icon = ICONS[c.icon] || CircleHelp;
                const active = category === c.value;
                return (
                  <button
                    type="button"
                    key={c.value}
                    onClick={() => setCategory(c.value)}
                    data-testid={`report-category-${c.value}`}
                    className={`text-left rounded-2xl border p-4 flex items-start gap-3 transition-all ${
                      active ? "border-terracotta-400 bg-terracotta-50 ring-2 ring-terracotta-400/40" : "border-forest-600/10 bg-white hover:border-forest-300 hover:bg-forest-50/40"
                    }`}
                  >
                    <span className={`grid place-items-center h-10 w-10 rounded-lg shrink-0 ${active ? "bg-terracotta-400 text-white" : "bg-forest-50 text-forest-600"}`}>
                      <Icon className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                    <span>
                      <span className="block font-semibold text-forest-900">{c.label}</span>
                      <span className="block text-xs text-charcoal-600 mt-0.5">{c.description}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Details */}
          <div className="space-y-6">
            <div>
              <Label htmlFor="title" className="text-forest-900 font-semibold">Give it a short title <span className="text-terracotta-500">*</span></Label>
              <Input id="title" data-testid="report-title-input" value={form.title} onChange={(e) => set("title", e.target.value)}
                placeholder="e.g., Uncomfortable comments during meetings"
                className="mt-2 bg-bone rounded-lg border-forest-600/15 focus-visible:ring-terracotta-400/50 p-5 text-base" />
            </div>

            <div>
              <Label htmlFor="description" className="text-forest-900 font-semibold">Tell us what happened <span className="text-terracotta-500">*</span></Label>
              <Textarea id="description" data-testid="report-description-input" value={form.description} onChange={(e) => set("description", e.target.value)}
                placeholder="Share as much or as little as you're comfortable with — what happened, when, and who was involved."
                rows={6} className="mt-2 bg-bone rounded-lg border-forest-600/15 focus-visible:ring-terracotta-400/50 p-4 text-base resize-none" />
            </div>

            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <Label className="text-forest-900 font-semibold">Which campus?</Label>
                <Select value={form.campus} onValueChange={(v) => set("campus", v)}>
                  <SelectTrigger data-testid="report-campus-select" className="mt-2 bg-bone rounded-lg border-forest-600/15 h-12 px-4">
                    <SelectValue placeholder="Select a campus" />
                  </SelectTrigger>
                  <SelectContent>
                    {(content.campuses || CAMPUSES).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="desired" className="text-forest-900 font-semibold">What outcome are you hoping for?</Label>
                <Input id="desired" data-testid="report-outcome-input" value={form.desired_outcome} onChange={(e) => set("desired_outcome", e.target.value)}
                  placeholder="Optional" className="mt-2 bg-bone rounded-lg border-forest-600/15 focus-visible:ring-terracotta-400/50 h-12 px-4" />
              </div>
            </div>
          </div>

          {/* Reporter info */}
          {!anonymous && (
            <div className="grid gap-6 sm:grid-cols-2 rounded-2xl bg-bone-100/60 border border-forest-600/10 p-6">
              <div>
                <Label htmlFor="name" className="text-forest-900 font-semibold">Your name</Label>
                <Input id="name" data-testid="report-name-input" value={form.reporter_name} onChange={(e) => set("reporter_name", e.target.value)}
                  placeholder="Optional" className="mt-2 bg-white rounded-lg border-forest-600/15 h-12 px-4" />
              </div>
              <div>
                <Label htmlFor="email" className="text-forest-900 font-semibold">Email for updates</Label>
                <Input id="email" type="email" data-testid="report-email-input" value={form.reporter_email} onChange={(e) => set("reporter_email", e.target.value)}
                  placeholder="you@patria.church" className="mt-2 bg-white rounded-lg border-forest-600/15 h-12 px-4" />
              </div>
            </div>
          )}

          {/* Meeting */}
          <div className="rounded-2xl bg-white border border-forest-600/10 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-start gap-3">
                <Calendar className="h-5 w-5 text-forest-600 mt-0.5" strokeWidth={1.5} />
                <div>
                  <p className="font-semibold text-forest-900">Request a facilitated conversation</p>
                  <p className="text-sm text-charcoal-600">A specialist can arrange a neutral, guided conversation.</p>
                </div>
              </div>
              <Switch checked={requestMeeting} onCheckedChange={setRequestMeeting} data-testid="meeting-toggle" />
            </div>
            {requestMeeting && (
              <div className="mt-4 pl-8">
                <Label className="text-forest-900 font-semibold text-sm">Preferred format</Label>
                <Select value={form.meeting_mode} onValueChange={(v) => set("meeting_mode", v)}>
                  <SelectTrigger data-testid="meeting-mode-select" className="mt-2 bg-bone rounded-lg border-forest-600/15 h-12 px-4 max-w-xs">
                    <SelectValue placeholder="Choose a format" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_person">In person</SelectItem>
                    <SelectItem value="virtual">Virtual (video call)</SelectItem>
                    <SelectItem value="phone">Phone call</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <Button type="submit" disabled={submitting} data-testid="report-submit-btn"
            className="w-full rounded-full bg-terracotta-400 hover:bg-terracotta-500 text-white py-6 text-base shadow-lg hover:-translate-y-0.5 transition-transform">
            {submitting ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Submitting…</> : "Submit my concern"}
          </Button>
        </form>
      </div>
    </Layout>
  );
}

function CopyField({ label, value, testid }) {
  const copy = () => { navigator.clipboard.writeText(value); toast.success(`${label} copied`); };
  return (
    <div className="rounded-xl bg-white border border-forest-600/10 p-3">
      <p className="text-[11px] uppercase tracking-wide text-charcoal-600">{label}</p>
      <div className="flex items-center justify-between mt-0.5">
        <span className="font-serif text-lg text-forest-900 font-semibold" data-testid={testid}>{value}</span>
        <button type="button" onClick={copy} className="text-forest-500 hover:text-terracotta-500" aria-label={`Copy ${label}`}>
          <Copy className="h-4 w-4" strokeWidth={1.5} />
        </button>
      </div>
    </div>
  );
}
